// ─────────────────────────────────────────────────────────────
// Αποθήκευση session — token + χρήστης στο localStorage.
// ΧΩΡΙΣ imports από api.ts: το api.ts κάνει import ΑΠΟ ΕΔΩ,
// οπότε οτιδήποτε αντίστροφο θα δημιουργούσε κυκλική εξάρτηση.
// ─────────────────────────────────────────────────────────────

const TOKEN_KEY = "erp_auth_token"
const USER_KEY = "erp_auth_user"

export type PermissionLevel = "none" | "read" | "full"
export type PermissionMap = Record<string, PermissionLevel>

export type AuthUser = {
  name: string
  email: string
  role: string
  /**
   * Ο χάρτης έρχεται ΑΠΟ ΤΟΝ SERVER και δεν συμπεραίνεται από τον ρόλο.
   * Ο Admin πειράζει δικαιώματα ανά χρήστη· ένα αντίγραφο των πακέτων εδώ θα
   * απέκλινε με το πρώτο πείραγμα και η οθόνη θα έλεγε άλλα από τον server.
   */
  permissions: PermissionMap
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(USER_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

/**
 * Ενημερώνει ΜΟΝΟ τον χρήστη, κρατώντας το token.
 *
 * Χρησιμοποιείται όταν ο διαχειριστής αλλάξει δικαιώματα: ο λογαριασμός είναι
 * ο ίδιος και η συνεδρία ισχύει — αυτό που άλλαξε είναι το τι επιτρέπεται.
 */
export function setStoredUser(user: AuthUser): boolean {
  try {
    localStorage.setItem(USER_KEY, JSON.stringify(user))

    return true
  } catch {
    // Quota/private mode. Επιστρέφει false ώστε ο καλών να ΜΗΝ ξαναφορτώσει
    // τη σελίδα: η αλλαγή δεν αποθηκεύτηκε, οπότε μετά την ανανέωση θα
    // φαινόταν πάλι «νέα» και η σελίδα θα ξαναφόρτωνε στο άπειρο.
    return false
  }
}

/** Άλλαξε ο ρόλος ή έστω ένα δικαίωμα; Ό,τι άλλο (όνομα, email) δεν μετράει. */
export function accessChanged(a: AuthUser | null, b: AuthUser): boolean {
  if (!a) return true
  if (a.role !== b.role) return true

  const keys = new Set([...Object.keys(a.permissions ?? {}), ...Object.keys(b.permissions ?? {})])
  for (const k of keys) {
    if ((a.permissions?.[k] ?? "none") !== (b.permissions?.[k] ?? "none")) return true
  }

  return false
}

export function setAuth(token: string, user: AuthUser): void {
  try {
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(USER_KEY, JSON.stringify(user))
  } catch {
    // Quota/private mode — το login θα «κρατήσει» μόνο για το τρέχον tab.
  }
}

/**
 * Καθαρίζει token, χρήστη ΚΑΙ όλα τα erp_* cache entries.
 *
 * Το cache (αποθήκη, ρυθμίσεις) γράφτηκε από συνδεδεμένο χρήστη — μετά
 * το logout δεν πρέπει να παραμένει αναγνώσιμο σε κοινόχρηστο υπολογιστή.
 */
export function clearAuth(): void {
  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith("erp_")) localStorage.removeItem(key)
    }
  } catch {
    // ignore
  }
}
